import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Settings, Sun, Moon, Smile, Info } from "lucide-react";
import SimpleDropdown from "../Common/SimpleDropdown";
import { useAuth } from "../Auth/AuthContext";
import { Link } from "react-router-dom";
import CommonApiService from '../../ApiServices/CommonApiService';
import moment from "moment";

const Topbar = () => {
    const dropdownRef = useRef(null);
    const [isDarkMode, setIsDarkMode] = useState(
        localStorage.getItem("theme") === "dark"
    );
    const [time, setTime] = useState(new Date().toLocaleTimeString());
    const [notifications, setNotifications] = useState([]);
    const [showNotifications, setShowNotifications] = useState(false);

    const Auth = useAuth();
    const user = Auth.authState.user;
    const role = Auth.authState.roles;
    const admin = role === "Admin";

    useEffect(() => {
        if (isDarkMode) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
        localStorage.setItem("theme", isDarkMode ? "dark" : "light");
    }, [isDarkMode]);

    useEffect(() => {
        fetchNotifications();

        const notificationInterval = setInterval(fetchNotifications, 10000);
        const timeInterval = setInterval(() => {
            setTime(new Date().toLocaleTimeString());
        }, 1000);

        return () => {
            clearInterval(notificationInterval);
            clearInterval(timeInterval);
        };
    }, []);

    const toggleDarkMode = () => {
        setIsDarkMode((prevMode) => !prevMode);
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return { message: "Good Morning", icon: <Sun size={20} className="text-yellow-500" /> };
        if (hour < 18) return { message: "Good Afternoon", icon: <Smile size={20} className="text-orange-500" /> };
        return { message: "Good Evening", icon: <Moon size={20} className="text-blue-500" /> };
    };

    const { message, icon } = getGreeting();


    const fetchNotifications = async () => {
        try {
            const response =
                role === "Admin"
                    ? await CommonApiService.getAllNotifications()
                    : await CommonApiService.getPublisherNotifications(user.id);
            setNotifications(response || []);
        } catch (error) {
            console.error("Error fetching notifications:", error.message);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const markNotificationsAsRead = async () => {
        try {

            await CommonApiService.updateNotifications(user.id);
        } catch (error) {
            console.error("Error marking notifications as read:", error.message);
        }
    };

    return (
        <motion.header
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`shadow-md p-4 flex items-center justify-between fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"}`}
        >
            <motion.h3 className="text-2xl md:text-3xl font-bold text-purple-700 capitalize">Dashboard</motion.h3>
            <motion.div className="hidden md:flex items-center gap-4 px-4 py-2 rounded-lg shadow-sm">
                {icon}
                <span>{message}, <strong>{user?.firstName}!</strong></span>
                <span className="font-mono">{time}</span>
            </motion.div>

            <nav className="relative flex items-center gap-4">
                <motion.button
                    className="p-2 rounded-full shadow-md transition bg-gray-100 dark:bg-gray-700"
                    onClick={toggleDarkMode}
                >
                    {isDarkMode ? <Sun size={22} className="text-yellow-500" /> : <Moon size={22} className="text-blue-500" />}
                </motion.button>
                <Link to="/adminsetting">
                    <Settings size={22} className="text-gray-600 dark:text-gray-300" />
                </Link>
                <motion.button
                    className="p-3 bg-gray-100 dark:bg-gray-800 rounded-full shadow-md transition-all relative"
                    onClick={async () => {
                        const toggleState = !showNotifications;
                        setShowNotifications(toggleState);
                        if (toggleState) {
                            await markNotificationsAsRead();  // only call when opening
                            await fetchNotifications();       // reload to reflect updates
                        }
                    }}
                >
                    <Bell size={24} className="text-gray-700 dark:text-gray-300" />
                    {notifications.some(n => !n.isRead) && (
                        <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
                    )}
                </motion.button>

                {showNotifications && (
                    <AnimatePresence>
                        <motion.div
                            ref={dropdownRef}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="absolute top-12 right-0 w-80 bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 z-50 max-h-96 overflow-y-auto"
                        >
                            <div className="p-4 border-b dark:border-gray-600">
                                <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Notifications</h4>
                            </div>
                            {notifications.length === 0 ? (
                                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                                    No new notifications
                                </div>
                            ) : (
                                <ul className="divide-y dark:divide-gray-700">
                                    {notifications.map((notif, index) => (
                                        <li
                                            key={index}
                                            className={`flex gap-2 items-start p-4 transition-all duration-200 border-l-4 ${!notif.isRead
                                                ? "border-blue-500 bg-blue-50 dark:bg-gray-700/50"
                                                : "border-transparent"
                                                } hover:bg-gray-100 dark:hover:bg-gray-800`}
                                        >
                                            {!notif.isRead && (
                                                <span className="w-2 h-2 mt-2 rounded-full bg-blue-500 animate-pulse shrink-0"></span>
                                            )}
                                            <div className="flex-1">
                                                <p
                                                    className={`text-sm font-semibold ${!notif.isRead ? "text-gray-800 dark:text-white" : "text-gray-500 dark:text-gray-400"
                                                        }`}
                                                >
                                                    {notif.title}
                                                </p>
                                                <p
                                                    className={`text-sm ${!notif.isRead ? "text-gray-700 dark:text-gray-300" : "text-gray-400 dark:text-gray-500"
                                                        }`}
                                                >
                                                    {notif.message}
                                                </p>
                                                <p className="text-xs text-gray-400 mt-1">{moment(notif.createdAt).fromNow()}</p>
                                            </div>
                                        </li>

                                    ))}
                                </ul>
                            )}
                        </motion.div>
                    </AnimatePresence>
                )}

                <SimpleDropdown />
            </nav>
        </motion.header>
    );
};

export default Topbar;